// ═══════════════════════════════════════════════════════════════════════════
// ReportViewerTab.js — Generic viewer for all Layer 3 paid reports.
// Handles: name entry → loading → collapsible section cards → share/copy.
// Used by ReportsStorePage for every report type.
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useRef } from "react";

const API = (process.env.REACT_APP_API_URL || "http://localhost:8000").replace(/\/$/, "");

// Matches JyotishPage.js's JT token system — white, one accent, plain Inter.
// Individual reports may pass their own `accentColor` (used sparingly — icon
// label, button, section chevron — never as a competing page background).
const RT = {
  page:     "#ffffff",
  card:     "#ffffff",
  cardWarm: "#fafafa",
  border:   "#e2e2e2",
  shadow:   "0 1px 3px rgba(0,0,0,0.08)",
  font:     "'Inter',sans-serif",
  text:     "#1a1a1a",
  textDim:  "#5a5a5a",
  accent:   "#c2560f",
};

// Section icon map — matches common section title keywords
const SECTION_ICONS = {
  "cosmic identity":   "🌌",
  "birth star":        "⭐",
  "soul":              "🔮",
  "natural strength":  "✨",
  "growth edge":       "🌱",
  "dharmic path":      "🔱",
  "authority":         "👑",
  "built to achieve":  "🎯",
  "dominant":          "⚡",
  "power":             "🔥",
  "timeline":          "📅",
  "purpose":           "🔱",
  "body":              "🌿",
  "energy":            "⚡",
  "career":            "🏆",
  "relationship":      "💞",
  "karmic":            "🌀",
  "compatibility":     "💑",
};

function getSectionIcon(title) {
  const t = title.toLowerCase();
  for (const [key, icon] of Object.entries(SECTION_ICONS)) {
    if (t.includes(key)) return icon;
  }
  return "✦";
}

// ── Loading shimmer ───────────────────────────────────────────────────────────
function ReportShimmer() {
  const shimmer = {
    background: "linear-gradient(90deg,#f0f0f0 25%,#e2e2e2 50%,#f0f0f0 75%)",
    backgroundSize: "200% 100%",
    animation: "shimmer 1.6s infinite",
    borderRadius: 8,
  };
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20, padding:"8px 0" }}>
      {[1,2,3,4,5,6].map(i => (
        <div key={i} style={{ display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ ...shimmer, height:18, width:`${38 + i*7}%` }} />
          <div style={{ ...shimmer, height:13, width:"100%" }} />
          <div style={{ ...shimmer, height:13, width:"88%" }} />
          <div style={{ ...shimmer, height:13, width:"94%" }} />
        </div>
      ))}
      <p style={{ textAlign:"center", color:RT.textDim, fontSize:"0.95rem", marginTop:4 }}>
        Preparing your reading... ~15 seconds
      </p>
    </div>
  );
}

// ── Name entry ────────────────────────────────────────────────────────────────
function NamePrompt({ reportName, onSubmit }) {
  const [name, setName] = useState("");
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16,
                  alignItems:"center", padding:"24px 0" }}>
      <div style={{ fontSize:"2.2rem" }}>✨</div>
      <p style={{ color:RT.text, textAlign:"center", fontSize:"1.02rem",
                  margin:0, lineHeight:1.6 }}>
        Your report opens with your name.
        <br/>
        <span style={{ color:RT.textDim, fontSize:"0.95rem" }}>
          "Prepared for [Name] · Born [Date] · [Place]"
        </span>
      </p>
      <input
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={e => e.key === "Enter" && name.trim() && onSubmit(name.trim())}
        placeholder="Enter your name"
        style={{ width:"100%", maxWidth:280, padding:"12px 16px",
                 background:"#ffffff", border:`1px solid ${RT.border}`,
                 borderRadius:10, color:RT.text, fontSize:"1rem",
                 outline:"none", textAlign:"center" }}
      />
      <button
        disabled={!name.trim()}
        onClick={() => onSubmit(name.trim())}
        style={{ padding:"12px 32px", borderRadius:24,
                 background: name.trim() ? RT.accent : "#f0f0f0",
                 border:"none", color: name.trim() ? "#fff" : RT.textDim,
                 fontSize:"1.0rem", fontWeight:700,
                 cursor: name.trim() ? "pointer" : "default", transition:"all 0.2s" }}>
        Generate — {reportName}
      </button>
    </div>
  );
}

// ── Report header ─────────────────────────────────────────────────────────────
function ReportHeader({ report, accentColor }) {
  const color = accentColor || RT.accent;

  return (
    <div style={{ borderRadius:16, padding:"20px",
                  background: RT.cardWarm, border: `1px solid ${RT.border}`, marginBottom:18,
                  boxShadow: RT.shadow }}>
      <div style={{ height:3, width:64, background:color,
        borderRadius:2, marginBottom:16 }} />
      <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
        <span style={{ fontSize:"2.2rem" }}>
          {report.character_emoji || "✨"}
        </span>
        <div>
          <div style={{ color, fontSize:"0.95rem", fontWeight:700,
                        fontFamily:RT.font, marginBottom:4 }}>
            {report.report_name}
          </div>
          <div style={{ color: RT.text, fontWeight:700, fontSize:"1.2rem",
                        fontFamily:RT.font }}>
            {report.name}
          </div>
        </div>
      </div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
        {[
          report.character    && { label:"Archetype",   value: report.character },
          report.lagna_rashi  && { label:"Lagna",        value: report.lagna_rashi },
          report.moon_rashi   && { label:"Moon",         value: report.moon_rashi },
          report.janma_nakshatra && { label:"Nakshatra",  value: report.janma_nakshatra },
        ].filter(Boolean).map((item, i) => (
          <div key={i} style={{ padding:"6px 12px", borderRadius:16,
                                background: "#ffffff", border: `1px solid ${RT.border}` }}>
            <span style={{ color: RT.textDim, fontSize:"0.95rem" }}>{item.label}: </span>
            <span style={{ color: RT.text, fontSize:"0.98rem", fontWeight:700 }}>{item.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Section card ──────────────────────────────────────────────────────────────
function SectionCard({ section, index, expanded, onToggle, accentColor }) {
  const color = accentColor || RT.accent;
  const icon  = getSectionIcon(section.title);

  return (
    <div onClick={onToggle} style={{
      borderRadius:16, overflow:"hidden", cursor:"pointer",
      border: `1px solid ${RT.border}`, background: RT.card,
      transition:"all 0.2s",
      boxShadow: expanded ? RT.shadow : "none",
      animation:`fadeInUp 0.35s ease ${index * 0.06}s both`,
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:12, padding:"18px 20px" }}>
        <span style={{ fontSize:"1.5rem", flexShrink:0 }}>{icon}</span>
        <div style={{ flex:1, color: RT.text,
                      fontWeight:700, fontSize:"1.05rem",
                      fontFamily:RT.font, lineHeight:1.3 }}>
          {section.title}
        </div>
        <span style={{ color,
                       fontSize:"1.2rem", flexShrink:0,
                       transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                       transition:"transform 0.2s" }}>›</span>
      </div>
      {expanded && (
        <div style={{ padding:"0 20px 22px",
                      borderTop: `1px solid ${RT.border}` }}>
          <p style={{ color: RT.text, lineHeight:1.85, fontSize:"1.15rem",
                      fontFamily:RT.font,
                      margin:"16px 0 0", whiteSpace:"pre-line" }}>
            {section.body}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Actions row ───────────────────────────────────────────────────────────────
function ReportActions({ report, onExpandAll, allExpanded }) {
  const [copied, setCopied] = useState(false);

  const copyText = () => {
    const lines = [
      report.report_name,
      `Prepared for ${report.name} · ${report.dob} · ${report.birth_city || ""}`,
      "",
      ...(report.sections || []).flatMap(s => [`── ${s.title} ──`, s.body, ""]),
      "Generated by VedSanga · vedsanga.com",
    ];
    navigator.clipboard.writeText(lines.join("\n")).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const btnBase = { flex:1, padding:"11px", borderRadius:22,
    background:"#ffffff", border:`1px solid ${RT.border}`,
    color:RT.accent, fontSize:"1rem", cursor:"pointer",
    fontFamily:RT.font, fontWeight:600, transition:"all 0.2s" };
  return (
    <div style={{ display:"flex", gap:8, marginBottom:18, flexWrap:"wrap" }}>
      <button onClick={onExpandAll} style={btnBase}>
        {allExpanded ? "Collapse All" : "Expand All"}
      </button>
      <button onClick={copyText}
        style={{ ...btnBase,
                 background: copied ? "rgba(34,197,94,0.08)" : "#ffffff",
                 borderColor: copied ? "rgba(34,197,94,0.4)" : RT.border,
                 color: copied ? "#15803d" : RT.accent }}>
        {copied ? "✓ Copied!" : "📋 Copy"}
      </button>
      <button onClick={() => {
        const text = `✨ ${report.report_name}\n\n${report.character} · ${report.moon_rashi}\n\nGenerated by VedSanga · vedsanga.com`;
        navigator.share ? navigator.share({ title: report.report_name, text })
                        : navigator.clipboard.writeText(text);
      }}
        style={btnBase}>
        🔗 Share
      </button>
    </div>
  );
}

// ── Main exported component ───────────────────────────────────────────────────
export default function ReportViewerTab({
  userId, noBirth, onNeedBirth,
  reportType,       // e.g. "power_purpose"
  reportEndpoint,   // e.g. "/jyotish/report/power-purpose"
  reportName,       // e.g. "Your Power & Purpose Reading"
  reportPrice,      // e.g. 99
  accentColor,      // e.g. "#f59e0b"
}) {
  const [phase,        setPhase]      = useState("intro");
  const [report,       setReport]     = useState(null);
  const [expanded,     setExpanded]   = useState({});
  const [allExpanded,  setAllExp]     = useState(false);
  const [payOrder,     setPayOrder]   = useState(null);   // Razorpay order for this report
  const [pendingName,  setPendingName] = useState("");    // name saved during payment
  const [payLoading,   setPayLoading] = useState(false);
  const [payError,     setPayError]   = useState("");
  const topRef = useRef(null);

  const color = accentColor || RT.accent;

  const loadRazorpay = () => new Promise(resolve => {
    if (document.getElementById("rzp-script")) { resolve(true); return; }
    const s = document.createElement("script");
    s.id = "rzp-script";
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });

  // Auto-load if already purchased and cached
  useEffect(() => {
    if (!userId || noBirth) return;
    fetch(`${API}${reportEndpoint}/${encodeURIComponent(userId)}`)
      .then(async r => {
        if (r.status === 402) return null; // not purchased yet — stay on intro
        return r.json();
      })
      .then(d => {
        if (d?.sections?.length > 0) {
          setReport(d);
          setExpanded({ 0:true, 1:true });
          setPhase("done");
        }
      })
      .catch(() => {});
  }, [userId, reportEndpoint]);

  const handleGenerate = async (name) => {
    setPhase("loading");
    setPendingName(name);
    topRef.current?.scrollIntoView({ behavior:"smooth" });
    try {
      const res = await fetch(
        `${API}${reportEndpoint}/${encodeURIComponent(userId)}?name=${encodeURIComponent(name)}`
      );
      if (res.status === 402) {
        // No access — create Razorpay order for this report
        const orderRes = await fetch(`${API}/jyotish/report/create-order`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId, report_type: reportType }),
        });
        const order = await orderRes.json();
        if (!order.order_id) { setPhase("error"); return; }
        setPayOrder(order);
        setPhase("payment");
        return;
      }
      const d = await res.json();
      if (d.error) { setPhase("error"); return; }
      setReport(d);
      setExpanded({ 0:true, 1:true });
      setPhase("done");
    } catch {
      setPhase("error");
    }
  };

  const handlePay = async () => {
    setPayLoading(true);
    setPayError("");
    const loaded = await loadRazorpay();
    if (!loaded) {
      setPayError("Could not load payment SDK. Check your internet.");
      setPayLoading(false);
      return;
    }
    const rzp = new window.Razorpay({
      key:         payOrder.key_id,
      amount:      payOrder.amount,
      currency:    payOrder.currency,
      order_id:    payOrder.order_id,
      name:        "VedSanga",
      description: payOrder.report_name,
      image:       "/logo192.png",
      theme:       { color },
      method:      { upi: true, card: true, netbanking: true, wallet: true },
      handler: async (response) => {
        try {
          const vRes = await fetch(`${API}/jyotish/report/verify-purchase`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              user_id:             userId,
              report_type:         reportType,
              razorpay_order_id:   response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature:  response.razorpay_signature,
            }),
          });
          const vData = await vRes.json();
          if (vData.ok) {
            handleGenerate(pendingName); // retry with access now granted
          } else {
            setPayError("Payment verified but access grant failed. Contact support.");
            setPhase("payment");
          }
        } catch {
          setPayError("Verification failed. Please contact support.");
          setPhase("payment");
        }
      },
      modal: { ondismiss: () => { setPhase("intro"); setPayLoading(false); } },
    });
    rzp.open();
    setPayLoading(false);
  };

  const toggleSection = (idx) =>
    setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }));

  const handleExpandAll = () => {
    if (allExpanded) { setExpanded({}); setAllExp(false); }
    else {
      const all = {};
      (report?.sections || []).forEach((_, i) => { all[i] = true; });
      setExpanded(all); setAllExp(true);
    }
  };

  useEffect(() => {
    if (!report?.sections) return;
    setAllExp(report.sections.every((_, i) => expanded[i]));
  }, [expanded, report]);

  if (noBirth) {
    return (
      <div style={{ textAlign:"center", padding:"40px 16px" }}>
        <div style={{ fontSize:"2rem", marginBottom:12 }}>🌟</div>
        <p style={{ color:RT.textDim, fontSize:"1rem", lineHeight:1.7 }}>
          Add your birth date and city to generate this report.
        </p>
        <button onClick={onNeedBirth}
          style={{ marginTop:12, padding:"12px 32px", borderRadius:24,
                   background:RT.accent,
                   border:"none", color:"#fff", fontSize:"1rem",
                   fontWeight:700, cursor:"pointer", fontFamily:RT.font }}>
          Add Birth Details
        </button>
      </div>
    );
  }

  return (
    <div ref={topRef} style={{ display:"flex", flexDirection:"column" }}>

      {/* Intro */}
      {phase === "intro" && (
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
          <div style={{ borderRadius:18, padding:"24px 20px",
                        background:RT.cardWarm,
                        border:`1px solid ${RT.border}`,
                        textAlign:"center",
                        boxShadow:RT.shadow }}>
            <div style={{ height:3, width:64, background:color,
              borderRadius:2, margin:"0 auto 16px" }} />
            <div style={{ fontFamily:RT.font, color:RT.text,
                          fontSize:"1.2rem", fontWeight:700, marginBottom:6 }}>
              {reportName}
            </div>
            <div style={{ color:RT.textDim, fontSize:"0.98rem",
              fontFamily:RT.font }}>
              One-time · Generated from your Janma Kundali
            </div>
          </div>
          <div style={{ borderRadius:16, padding:"18px",
                        background:RT.card,
                        border:`1px solid ${RT.border}`,
                        textAlign:"center" }}>
            <div style={{ color, fontSize:"1.5rem", fontWeight:700,
                          fontFamily:RT.font, marginBottom:14 }}>
              ₹{reportPrice}
            </div>
            <button onClick={() => setPhase("name")}
              style={{ width:"100%", padding:"14px", borderRadius:22,
                       background:color,
                       border:"none", color:"#ffffff", fontSize:"1.0rem",
                       fontWeight:700, cursor:"pointer",
                       fontFamily:RT.font,
                       boxShadow:`0 4px 20px ${color}44` }}>
              Generate My Report →
            </button>
          </div>
        </div>
      )}

      {/* Name entry */}
      {phase === "name" && (
        <NamePrompt reportName={`₹${reportPrice}`} onSubmit={handleGenerate} />
      )}

      {/* Loading */}
      {phase === "loading" && <ReportShimmer />}

      {/* Payment */}
      {phase === "payment" && payOrder && (
        <div style={{ display:"flex", flexDirection:"column", gap:16, padding:"8px 0" }}>
          <div style={{ borderRadius:18, padding:"28px 20px",
                        background:RT.cardWarm,
                        border:`1px solid ${RT.border}`, textAlign:"center" }}>
            <div style={{ fontSize:"2.4rem", marginBottom:12 }}>🔐</div>
            <div style={{ fontFamily:RT.font,
                          color:RT.text, fontSize:"1.1rem", fontWeight:700, marginBottom:6 }}>
              {payOrder.report_name}
            </div>
            <div style={{ color:RT.textDim, fontSize:"0.98rem", marginBottom:20 }}>
              One-time purchase · Never expires · Permanent access
            </div>
            <div style={{ fontSize:"2.4rem", fontWeight:700,
                          fontFamily:RT.font,
                          color, marginBottom:24 }}>
              ₹{payOrder.amount / 100}
            </div>
            {payError && (
              <div style={{ color:"#b91c1c", fontSize:"0.95rem",
                            marginBottom:14, padding:"8px 12px",
                            background:"rgba(239,68,68,0.06)", borderRadius:8 }}>
                {payError}
              </div>
            )}
            <button
              onClick={handlePay}
              disabled={payLoading}
              style={{ width:"100%", padding:"16px", borderRadius:24,
                       background: payLoading ? "#f0f0f0" : color,
                       border:"none", color: payLoading ? RT.textDim : "#ffffff",
                       fontSize:"1.05rem", fontWeight:700, cursor: payLoading ? "default" : "pointer",
                       fontFamily:RT.font,
                       boxShadow: payLoading ? "none" : `0 4px 20px ${color}44` }}>
              {payLoading ? "Opening payment..." : `Pay ₹${payOrder.amount / 100} — UPI / Card / NetBanking`}
            </button>
            <p style={{ color:RT.textDim, fontSize:"0.9rem",
                        marginTop:12, marginBottom:0 }}>
              🔒 Secured by Razorpay · Report unlocked permanently after payment
            </p>
          </div>
          <button
            onClick={() => setPhase("intro")}
            style={{ background:"transparent", border:"none",
                     color:RT.textDim, fontSize:"0.98rem",
                     cursor:"pointer", padding:"8px 0", textAlign:"center" }}>
            ← Go back
          </button>
        </div>
      )}

      {/* Error */}
      {phase === "error" && (
        <div style={{ textAlign:"center", padding:"40px 16px" }}>
          <div style={{ fontSize:"2rem", marginBottom:12 }}>⚠️</div>
          <p style={{ color:RT.textDim, fontSize:"1rem" }}>
            Something went wrong. Please try again.
          </p>
          <button onClick={() => setPhase("intro")}
            style={{ marginTop:12, padding:"10px 28px", borderRadius:24,
                     background:"#ffffff", border:`1px solid ${RT.border}`,
                     color:RT.text, fontSize:"1rem", cursor:"pointer" }}>
            Try Again
          </button>
        </div>
      )}

      {/* Done */}
      {phase === "done" && report && (
        <div>
          <ReportHeader report={report} accentColor={accentColor} />
          <ReportActions report={report} onExpandAll={handleExpandAll} allExpanded={allExpanded} />
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            {report.sections.map((s, i) => (
              <SectionCard key={i} section={s} index={i}
                expanded={!!expanded[i]} onToggle={() => toggleSection(i)}
                accentColor={accentColor} />
            ))}
          </div>
          <div style={{ textAlign:"center", marginTop:22, marginBottom:4 }}>
            <button onClick={() => { setPhase("name"); setReport(null); }}
              style={{ padding:"8px 20px", borderRadius:20, background:"transparent",
                       border:`1px solid ${RT.border}`,
                       color:RT.textDim, fontSize:"0.95rem", cursor:"pointer" }}>
              Regenerate with different name
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeInUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer  { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
      `}</style>
    </div>
  );
}
